import { Link, NavLink, Outlet } from 'react-router-dom'
import { useEffect, useRef, useState } from 'react'
import {
  BarChart3, Building2, ChevronRight, FileDown, FileText, Wallet
} from 'lucide-react'
import {
  useDocumentTemplates,
  useOperationsOverview,
  useOperationsReport,
  usePreviewDocumentTemplates,
  useUpdateDocumentTemplates
} from '@/lib/queries.js'
import { Card, CardBody, CardFooter, CardHeader, CardTitle, PageHeader } from '@/components/ui/card.jsx'
import { Button } from '@/components/ui/button.jsx'
import { Badge } from '@/components/ui/badge.jsx'
import { Alert, Spinner } from '@/components/ui/feedback.jsx'
import { humanMessage } from '@/lib/errors.js'
import { cn } from '@/lib/utils.js'
import { money } from '@/lib/format.js'
import { DataExportCard } from './DataExportSettings.jsx'

/**
 * The Operations zone (Phase 9 Session A — D9.1, D9.2, D9.3).
 *
 * ── WHAT THIS ZONE IS FOR ───────────────────────────────────────────────────
 * A contractor should be able to hand the whole day-to-day CRM to a sales hire
 * without also handing over financials, payroll, reporting, data export and
 * document-template configuration. Everything in here is the back office; the
 * pipeline is still the product.
 *
 * ── THE GATE IS NOT HERE ────────────────────────────────────────────────────
 * These screens render for a user the shell already decided has the grant, and
 * App.jsx bounces anyone else back to the Pipeline. Neither is the boundary:
 * every call these screens make is server-gated and answers 403
 * `operations_access_required` without the grant, however the client got here.
 * D9.3, verbatim: hiding the nav is courtesy, not security.
 *
 * ── THREE OF THE FOUR SECTIONS ARE PLACEHOLDERS, AND SAY SO ─────────────────
 * Reports is Session B. Templates and Payroll are later still. They occupy
 * their navigation slots now because the zone's shape is the deliverable, and
 * each one states plainly that it is not live — a nav item that does nothing is
 * worse than one that explains itself, and a placeholder that LOOKS live is
 * worse than both (the L2 flag-honesty lesson: advertising a feature with
 * nothing behind it is how a defect ships).
 */

const SECTIONS = [
  {
    key: 'reports',
    to: '/operations/reports',
    label: 'Reports',
    icon: BarChart3,
    blurb: 'How the month is going, over the work you already have in MBOS.'
  },
  {
    key: 'data_export',
    to: '/operations/data-export',
    label: 'Data export',
    icon: FileDown,
    blurb: 'Your clients, jobs and invoices as CSV files. Free, any time.'
  },
  {
    key: 'templates',
    to: '/operations/templates',
    label: 'Document templates',
    icon: FileText,
    blurb: 'How your estimate and invoice PDFs look to a customer.'
  },
  {
    key: 'payroll',
    to: '/operations/payroll',
    label: 'Payroll',
    icon: Wallet,
    blurb: 'Paying your crew, and tracking the hours behind it.'
  }
]

/**
 * The zone's frame: heading, section tabs, and the child route.
 *
 * Mobile-first (D8). The section switcher is a horizontally scrollable row of
 * pills on a phone and the same row with more air on desktop — deliberately not
 * a second sidebar, which at 375px would either eat the screen or hide the
 * thing it navigates.
 */
export function OperationsLayout () {
  return (
    <>
      <PageHeader
        title="Operations"
        description="Reporting, exports, document templates and payroll. Only people you grant access to can see this."
      />

      <nav
        className="-mx-4 mb-5 overflow-x-auto px-4 sm:mx-0 sm:px-0"
        aria-label="Operations sections"
      >
        <div className="flex w-max gap-2 pb-1">
          {/* `end` on Overview only, so it stops being active the moment a
              child route is open rather than staying lit alongside it. */}
          <SectionPill to="/operations" end label="Overview" icon={Building2} />
          {SECTIONS.map(section => (
            <SectionPill
              key={section.key}
              to={section.to}
              label={section.label}
              icon={section.icon}
            />
          ))}
        </div>
      </nav>

      <Outlet />
    </>
  )
}

function SectionPill ({ to, label, icon: Icon, end }) {
  return (
    <NavLink
      to={to}
      end={end}
      className={({ isActive }) => cn(
        'flex min-h-tap items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-medium transition-colors',
        isActive
          ? 'border-brand bg-brand-subtle text-brand'
          : 'border-line bg-surface text-ink-muted hover:text-ink'
      )}
    >
      <Icon className="size-4 shrink-0" aria-hidden="true" />
      {label}
    </NavLink>
  )
}

/**
 * The zone's landing screen.
 *
 * Deliberately NOT a dashboard of numbers. D9.2 puts reporting summary cards
 * here "once reporting exists", and it does not exist yet — so this is a map of
 * the zone with each section's real state, and nothing that could be mistaken
 * for a figure.
 */
export function OperationsOverview () {
  const { data, isPending, error } = useOperationsOverview()
  const stateByKey = new Map((data?.sections ?? []).map(s => [s.key, s.state]))

  return (
    <div className="space-y-5">
      {error && <Alert tone="danger">{humanMessage(error)}</Alert>}

      {isPending
        ? <div className="flex justify-center py-16"><Spinner label="Loading Operations" /></div>
        : (
          <>
            <ul className="grid gap-3 sm:grid-cols-2" data-testid="operations-sections">
              {SECTIONS.map(section => {
                const state = stateByKey.get(section.key) ?? 'planned'
                return (
                  <li key={section.key}>
                    <NavLink
                      to={section.to}
                      className="flex h-full items-start gap-3 rounded-lg border border-line bg-surface p-4 transition-colors hover:border-brand hover:bg-surface-sunken"
                      data-testid={`operations-section-${section.key}`}
                    >
                      <section.icon className="mt-0.5 size-5 shrink-0 text-ink-subtle" aria-hidden="true" />
                      <span className="min-w-0 flex-1">
                        <span className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-medium text-ink">{section.label}</span>
                          {state === 'planned' && <Badge tone="neutral">Planned</Badge>}
                        </span>
                        <span className="mt-1 block text-sm/relaxed text-ink-muted">{section.blurb}</span>
                      </span>
                      <ChevronRight className="mt-0.5 size-4 shrink-0 text-ink-subtle" aria-hidden="true" />
                    </NavLink>
                  </li>
                )
              })}
            </ul>

            {data?.access?.source === 'owner' && (
              <p className="text-sm text-ink-muted">
                You see Operations because you own this workspace. To let someone
                else in, use Workspace users in Settings.
              </p>
            )}
          </>
          )}
    </div>
  )
}

/**
 * Reports — Phase 9 Session B, `reporting_basic`.
 */
export function OperationsReports () {
  const [range, setRange] = useState('mtd')
  const [custom, setCustom] = useState({ start_date: '', end_date: '' })
  const filters = range === 'custom'
    ? { range, start_date: custom.start_date, end_date: custom.end_date }
    : { range }
  const { data: report, isPending, error } = useOperationsReport(filters)

  return (
    <div className="space-y-5" data-testid="operations-reports">
      <Card>
        <CardBody className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
          <div className="min-w-0">
            <h2 className="text-sm font-semibold text-ink">Reporting period</h2>
            {report?.range && (
              <p className="mt-1 text-sm text-ink-muted" data-testid="report-boundaries">
                {report.range.start_date} through {report.range.end_date}, UTC
              </p>
            )}
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex flex-wrap gap-2" role="group" aria-label="Report date range">
              {[
                ['mtd', 'MTD'],
                ['last_30_days', '30 days'],
                ['qtd', 'QTD'],
                ['ytd', 'YTD'],
                ['custom', 'Custom']
              ].map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setRange(value)}
                  className={cn(
                    'min-h-tap rounded-full border px-3 py-1.5 text-sm font-medium transition-colors',
                    range === value
                      ? 'border-brand bg-brand-subtle text-brand'
                      : 'border-line bg-surface text-ink-muted hover:text-ink'
                  )}
                >
                  {label}
                </button>
              ))}
            </div>
            {range === 'custom' && (
              <div className="grid gap-2 sm:grid-cols-2">
                <label className="text-xs font-medium text-ink-muted">
                  Start
                  <input
                    className="mt-1 min-h-tap w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink"
                    type="date"
                    value={custom.start_date}
                    onChange={event => setCustom(c => ({ ...c, start_date: event.target.value }))}
                  />
                </label>
                <label className="text-xs font-medium text-ink-muted">
                  End
                  <input
                    className="mt-1 min-h-tap w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink"
                    type="date"
                    value={custom.end_date}
                    onChange={event => setCustom(c => ({ ...c, end_date: event.target.value }))}
                  />
                </label>
              </div>
            )}
          </div>
        </CardBody>
      </Card>

      {error && <Alert tone="danger">{humanMessage(error)}</Alert>}
      {isPending && <div className="flex justify-center py-16"><Spinner label="Loading reports" /></div>}

      {report && (
        <>
          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Billing report cards">
            <MetricCard label="Invoice total" value={money(report.billing.invoice_total)} to={report.billing.drilldowns.invoices} />
            <MetricCard label="Collected" value={money(report.billing.collected_total)} to={report.billing.drilldowns.invoices} />
            <MetricCard label="Outstanding" value={money(report.billing.outstanding_balance)} to={report.billing.drilldowns.outstanding} />
            <MetricCard label="Overdue" value={money(report.billing.overdue_balance)} to={report.billing.drilldowns.overdue} />
          </section>

          <section className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4" aria-label="Activity report cards">
            <MetricCard label="Estimates sent" value={report.sales.estimates_sent} to={report.sales.drilldowns.sent} />
            <MetricCard label="Accepted" value={report.sales.estimates_accepted} detail={money(report.sales.accepted_estimate_value)} to={report.sales.drilldowns.accepted} />
            <MetricCard label="Declined" value={report.sales.estimates_declined} to={report.sales.drilldowns.declined} />
            <MetricCard label="Acceptance rate" value={percent(report.sales.acceptance_rate)} to={report.sales.drilldowns.accepted} />
            <MetricCard label="Jobs created" value={report.work.jobs_created} to={report.work.drilldowns.created} />
            <MetricCard label="Jobs completed" value={report.work.jobs_completed} to={report.work.drilldowns.completed} />
            <MetricCard label="Active jobs" value={report.work.currently_active_jobs} to={report.work.drilldowns.active} />
            <MetricCard label="Pipeline value" value={money(report.pipeline.estimated_value)} detail={`${report.pipeline.open_count} open`} to={report.pipeline.drilldown} />
          </section>

          <div className="grid gap-4 xl:grid-cols-3">
            <ReportTable
              title="Pipeline"
              columns={['Stage', 'Open', 'Value']}
              rows={report.pipeline.stages.map(stage => ({
                key: stage.stage_id,
                to: stage.drilldown,
                cells: [stage.stage_name, stage.count, money(stage.estimated_value)]
              }))}
            />
            <ReportTable
              title="Payments"
              columns={['Method', 'Count', 'Total']}
              rows={report.collections.payments_by_method.map(row => ({
                key: row.method,
                to: row.drilldown,
                cells: [humanLabel(row.method), row.count, money(row.total)]
              }))}
              empty="No payments in this period"
            />
            <ReportTable
              title="Aging"
              columns={['Bucket', 'Invoices', 'Balance']}
              rows={report.collections.invoice_aging.map(row => ({
                key: row.key,
                to: row.drilldown,
                cells: [row.label, row.count, money(row.balance)]
              }))}
            />
          </div>
        </>
      )}
    </div>
  )
}

function MetricCard ({ label, value, detail, to }) {
  return (
    <Link
      to={to}
      className="block rounded-lg border border-line bg-surface p-4 shadow-card transition-colors hover:border-brand hover:bg-surface-sunken"
      data-testid={`report-card-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <span className="block text-xs font-medium uppercase text-ink-muted">{label}</span>
      <span className="mt-2 block text-2xl font-semibold text-ink">{value}</span>
      {detail && <span className="mt-1 block text-sm text-ink-muted">{detail}</span>}
    </Link>
  )
}

function ReportTable ({ title, columns, rows, empty = 'No rows' }) {
  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <div className="overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-surface-sunken text-left text-xs font-medium uppercase text-ink-muted">
            <tr>
              {columns.map(column => <th key={column} className="px-4 py-2">{column}</th>)}
            </tr>
          </thead>
          <tbody className="divide-y divide-line">
            {rows.length === 0
              ? (
                <tr>
                  <td className="px-4 py-4 text-ink-muted" colSpan={columns.length}>{empty}</td>
                </tr>
                )
              : rows.map(row => (
                <tr key={row.key}>
                  {row.cells.map((cell, index) => (
                    <td key={index} className="px-4 py-2 text-ink">
                      {index === 0
                        ? <Link className="font-medium text-brand hover:underline" to={row.to}>{cell}</Link>
                        : <span className="tabular">{cell}</span>}
                    </td>
                  ))}
                </tr>
              ))}
          </tbody>
        </table>
      </div>
    </Card>
  )
}

function percent (value) {
  return `${Math.round(Number(value ?? 0) * 100)}%`
}

function humanLabel (value) {
  return String(value || 'other').replace(/_/g, ' ').replace(/^\w/, c => c.toUpperCase())
}

const CONTACT_LABELS = {
  business_phone: 'Business phone',
  business_email: 'Business email',
  address: 'Address',
  license_number: 'License number',
  hours: 'Hours',
  website: 'Website'
}

const TEMPLATE_COPY_FIELDS = [
  ['terms_text', 'Terms block', 'Terms shown on both estimates and invoices.'],
  ['footer_text', 'Footer text', 'Short copy at the bottom of both document types.'],
  ['estimate_intro_text', 'Estimate intro', 'Optional copy before the estimate line items.'],
  ['estimate_outro_text', 'Estimate closing', 'Optional copy after the estimate terms.'],
  ['invoice_intro_text', 'Invoice intro', 'Optional copy before the invoice line items.'],
  ['invoice_outro_text', 'Invoice closing', 'Optional copy after the invoice terms.']
]

function editorDefaults (data) {
  const saved = data?.document_templates ?? {}
  return {
    logo_placement: saved.logo_placement ?? 'left',
    accent_color: saved.accent_color ?? data?.branding?.default_accent_color ?? '#0F766E',
    visible_contact_fields: saved.visible_contact_fields ?? Object.keys(CONTACT_LABELS),
    terms_text: saved.terms_text ?? '',
    footer_text: saved.footer_text ?? '',
    estimate_intro_text: saved.estimate_intro_text ?? '',
    estimate_outro_text: saved.estimate_outro_text ?? '',
    invoice_intro_text: saved.invoice_intro_text ?? '',
    invoice_outro_text: saved.invoice_outro_text ?? ''
  }
}

/**
 * D9.2's constrained editor. The preview is a Blob returned by the same API
 * renderer used for signed estimates and invoice attachments; there is no
 * second browser-side document layout to drift.
 */
export function OperationsTemplates () {
  const templates = useDocumentTemplates()
  const update = useUpdateDocumentTemplates()
  const preview = usePreviewDocumentTemplates()
  const [form, setForm] = useState(null)
  const [customized, setCustomized] = useState(false)
  const [documentType, setDocumentType] = useState('estimate')
  const [previewUrl, setPreviewUrl] = useState(null)
  const [saved, setSaved] = useState(false)
  const previewUrlRef = useRef(null)

  useEffect(() => {
    if (templates.data) {
      setForm(editorDefaults(templates.data))
      setCustomized(Boolean(templates.data.configured))
    }
  }, [templates.data])

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
  }, [])

  const previewPayload = form ? JSON.stringify({ customized, form }) : ''
  useEffect(() => {
    if (!form) return
    let cancelled = false
    const timeout = setTimeout(async () => {
      try {
        const blob = await preview.mutateAsync({
          documentType,
          documentTemplates: customized ? form : {}
        })
        if (cancelled) return
        const next = URL.createObjectURL(blob)
        if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current)
        previewUrlRef.current = next
        setPreviewUrl(next)
      } catch {
        // The hook exposes the error below. Keeping the last successful PDF on
        // screen makes a transient preview failure less disruptive to editing.
      }
    }, 350)
    return () => {
      cancelled = true
      clearTimeout(timeout)
    }
  // `previewPayload` is the deliberate deep dependency for this plain object.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [documentType, previewPayload])

  async function save () {
    setSaved(false)
    await update.mutateAsync(customized ? form : {})
    setSaved(true)
  }

  async function reset () {
    setSaved(false)
    const data = await update.mutateAsync({})
    setForm(editorDefaults(data))
    setCustomized(false)
  }

  function editForm (updater) {
    setSaved(false)
    setCustomized(true)
    setForm(updater)
  }

  if (templates.isPending || !form) {
    return <div className="flex justify-center py-16"><Spinner label="Loading document templates" /></div>
  }

  const error = templates.error ?? update.error ?? preview.error

  return (
    <div className="space-y-5" data-testid="operations-templates">
      <div>
        <div className="flex flex-wrap items-center gap-2">
          <h2 className="text-lg font-semibold text-ink">Document templates</h2>
          <Badge tone={templates.data?.configured ? 'brand' : 'neutral'}>
            {templates.data?.configured ? 'Customized' : 'Standard layout'}
          </Badge>
        </div>
        <p className="mt-1 text-sm text-ink-muted">
          These settings apply to signed estimate PDFs and invoice PDF attachments.
          Copy is stored as plain text and rendered inert; page code is never accepted.
        </p>
      </div>

      {error && <Alert tone="danger">{humanMessage(error)}</Alert>}
      {saved && <Alert tone="success">Document template preferences saved.</Alert>}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1fr)_minmax(28rem,0.9fr)]">
        <Card>
          <CardHeader>
            <CardTitle>Template preferences</CardTitle>
          </CardHeader>
          <CardBody className="space-y-5">
            <div className="grid gap-4 sm:grid-cols-2">
              <label className="text-sm font-medium text-ink">
                Logo placement
                <select
                  value={form.logo_placement}
                  onChange={event => editForm(current => ({ ...current, logo_placement: event.target.value }))}
                  className="mt-1 min-h-tap w-full rounded-md border border-line bg-surface px-3 py-2 text-sm"
                  data-testid="template-logo-placement"
                >
                  <option value="left">Left</option>
                  <option value="center">Centered</option>
                  <option value="right">Right</option>
                  <option value="hidden">Hide logo</option>
                </select>
                {!templates.data?.branding?.has_logo && (
                  <span className="mt-1 block text-xs font-normal text-ink-muted">
                    No workspace logo is configured; the business name remains visible.
                  </span>
                )}
              </label>

              <label className="text-sm font-medium text-ink">
                Accent color
                <span className="mt-1 flex min-h-tap items-center gap-2 rounded-md border border-line bg-surface px-2">
                  <input
                    type="color"
                    value={form.accent_color}
                    onChange={event => editForm(current => ({ ...current, accent_color: event.target.value.toUpperCase() }))}
                    className="size-8 cursor-pointer border-0 bg-transparent p-0"
                    aria-label="Accent color picker"
                  />
                  <input
                    value={form.accent_color}
                    onChange={event => editForm(current => ({ ...current, accent_color: event.target.value }))}
                    maxLength={7}
                    className="min-w-0 flex-1 bg-transparent font-mono text-sm outline-none"
                    aria-label="Accent color hex"
                    data-testid="template-accent-color"
                  />
                </span>
              </label>
            </div>

            <fieldset>
              <legend className="text-sm font-medium text-ink">Contact fields shown</legend>
              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                {Object.entries(CONTACT_LABELS).map(([key, label]) => (
                  <label key={key} className="flex min-h-tap items-center gap-2 rounded-md border border-line px-3 py-2 text-sm text-ink">
                    <input
                      type="checkbox"
                      checked={form.visible_contact_fields.includes(key)}
                      onChange={event => editForm(current => ({
                        ...current,
                        visible_contact_fields: event.target.checked
                          ? [...current.visible_contact_fields, key]
                          : current.visible_contact_fields.filter(field => field !== key)
                      }))}
                    />
                    {label}
                  </label>
                ))}
              </div>
            </fieldset>

            {TEMPLATE_COPY_FIELDS.map(([key, label, help]) => (
              <label key={key} className="block text-sm font-medium text-ink">
                {label}
                <span className="ml-2 text-xs font-normal text-ink-muted">{help}</span>
                <textarea
                  value={form[key]}
                  onChange={event => editForm(current => ({ ...current, [key]: event.target.value }))}
                  maxLength={templates.data?.options?.text_limits?.[key]}
                  rows={key === 'terms_text' ? 5 : 3}
                  className="mt-1 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink"
                  data-testid={`template-${key}`}
                />
              </label>
            ))}
          </CardBody>
          <CardFooter>
            <Button type="button" onClick={save} loading={update.isPending}>Save preferences</Button>
            <Button type="button" variant="secondary" onClick={reset} disabled={update.isPending}>
              Use standard layout
            </Button>
          </CardFooter>
        </Card>

        <Card className="h-fit xl:sticky xl:top-4">
          <CardHeader className="flex-row items-center justify-between gap-3">
            <CardTitle>Live PDF preview</CardTitle>
            <div className="flex rounded-md border border-line p-0.5" role="group" aria-label="Preview document type">
              {['estimate', 'invoice'].map(type => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setDocumentType(type)}
                  className={cn(
                    'rounded px-3 py-1.5 text-xs font-medium capitalize',
                    documentType === type ? 'bg-brand text-brand-ink' : 'text-ink-muted'
                  )}
                >
                  {type}
                </button>
              ))}
            </div>
          </CardHeader>
          <CardBody>
            <div className="relative min-h-[42rem] overflow-hidden rounded-md border border-line bg-surface-sunken">
              {preview.isPending && (
                <div className="absolute inset-x-0 top-0 z-10 flex justify-center bg-surface/90 py-2">
                  <Spinner label="Rendering PDF preview" />
                </div>
              )}
              {previewUrl
                ? (
                  <object
                    data={previewUrl}
                    type="application/pdf"
                    className="h-[42rem] w-full"
                    data-testid="template-preview"
                    aria-label={`${documentType} PDF preview`}
                  >
                    <a href={previewUrl}>Open the PDF preview</a>
                  </object>
                  )
                : <div className="flex min-h-[42rem] items-center justify-center"><Spinner label="Rendering PDF preview" /></div>}
            </div>
            <p className="mt-2 text-xs text-ink-muted">
              Sample work and pricing; the PDF itself comes from the production document renderer.
            </p>
          </CardBody>
        </Card>
      </div>
    </div>
  )
}

/**
 * The two co-branded SurePayroll signup links (D9.2 / §5, confirmed by Rob
 * 2026-08-06). Both carry partner code U3B7; the only difference is the `ntp`
 * no-tax-processing flag.
 *
 * Tax filing included is the DEFAULT and the primary button, because §5 says
 * the planned handoff deliberately keeps tax withholding and compliance filings
 * on SurePayroll's side. No-tax-processing is offered as a deliberate secondary
 * choice for a contractor who already files their own payroll taxes — offered,
 * not hidden, because someone who needs it and cannot find it will sign up on
 * the wrong plan.
 */
const SUREPAYROLL_LINKS = Object.freeze({
  taxFilingIncluded: 'https://start.runpayroll.com/?partnerCode=U3B7&btype=bus&ntp=false&sendto=signup',
  noTaxProcessing: 'https://start.runpayroll.com/?partnerCode=U3B7&btype=bus&ntp=true&sendto=signup'
})

/**
 * Payroll (D9.2, §5) — the link-out, plus a status read.
 *
 * ── WHAT CHANGED IN PART 1, AND WHAT DELIBERATELY DID NOT ───────────────────
 * Session A's copy already promised "a sign-up link from this page" and there
 * was no link. That promise is now kept. The copy itself is barely touched,
 * because §5's shape was already confirmed and re-litigating settled wording is
 * how it drifts.
 *
 * ── TWO INDEPENDENT FACTS, AND CONFLATING THEM WOULD LIE ────────────────────
 * `section.state` (deployment: is Payroll built?) is still `planned`, because
 * Part 2's time tracking does not exist. `payroll.status` (tenant: are THEY a
 * SurePayroll customer?) may already be `active`. A tenant can therefore be
 * legitimately active while the section is legitimately planned, and this
 * screen has to say both without implying either is the other — hence the
 * `active` branch below promises time tracking is "not built yet" rather than
 * congratulating them on a working feature.
 *
 * ── COLLECTS NOTHING ────────────────────────────────────────────────────────
 * No EIN, no state tax registration, no employee data, per D9.2's explicit
 * instruction. There is no form on this screen at all — two anchors and some
 * prose. That is the entire surface, by design.
 */
export function OperationsPayroll () {
  const { data } = useOperationsOverview()
  const status = data?.payroll?.status ?? 'not_enabled'
  const isActive = status === 'active'

  return (
    <PlaceholderSection
      icon={Wallet}
      title="Payroll"
      testId="operations-payroll"
      lead={isActive
        ? 'Your SurePayroll account is on file. Time tracking is still on the way.'
        : 'Not switched on for this workspace yet.'}
    >
      <p>
        Payroll is planned as a SurePayroll-backed opt-in add-on, separate from
        your MBOS plan. Current pricing is $85 a month for 1 to 9 employees,
        $129 for 10 to 20, $179 for 21 to 35, $229 for 36 to 50, and above 50 we
        will quote you.
      </p>
      <p>
        Two halves, and it is worth being straight about which is which. Pay
        runs, tax withholding, filings and direct deposit happen on SurePayroll,
        our payroll provider, which you reach through the sign-up link below.
        Timesheets, hours, and putting those hours against the right job are what
        MBOS builds itself.
      </p>

      {isActive
        ? (
          <p data-testid="payroll-active-note">
            Because payroll runs on SurePayroll, pay runs and pay stubs live
            there rather than here — sign in to SurePayroll for those. The MBOS
            half, tracking hours against jobs, is not built yet. We will guide
            you through it when it ships, and we will not ask for tax or employee
            details before then.
          </p>
          )
        : (
          <>
            <div className="flex flex-col gap-2 pt-1 sm:flex-row" data-testid="payroll-signup-links">
              <Button asChild>
                <a href={SUREPAYROLL_LINKS.taxFilingIncluded} target="_blank" rel="noreferrer noopener">
                  Sign up with SurePayroll
                </a>
              </Button>
              <Button asChild variant="secondary">
                <a href={SUREPAYROLL_LINKS.noTaxProcessing} target="_blank" rel="noreferrer noopener">
                  Sign up without tax filing
                </a>
              </Button>
            </div>
            <p className="text-xs">
              The first includes tax filing, which is what most contractors want.
              Choose the second only if you already file your own payroll taxes.
              Both open SurePayroll in a new tab — you sign up with them
              directly, and MBOS never asks for your EIN, state tax registration
              or employee details.
            </p>
            <p>
              Once you have signed up, tell us and we will switch payroll on for
              this workspace. SurePayroll cannot tell us automatically, so it is
              a quick word rather than something that happens by itself.
            </p>
          </>
          )}
    </PlaceholderSection>
  )
}

/**
 * Data export — RELOCATED here from Settings, not rebuilt (D9.2).
 *
 * The same `DataExportCard` component that shipped in Session 1c-A, imported
 * from where it already lives. What actually moved is the canonical API path
 * (`/api/operations/data-exports`) and, with it, the gate: a member without the
 * grant can no longer export the book of business, which was the point.
 */
export function OperationsDataExport () {
  return (
    <div className="space-y-4" data-testid="operations-data-export">
      <DataExportCard />
      <p className="text-sm text-ink-muted">
        This used to live in Settings. It sits here now because your client, job
        and invoice history is business data rather than workspace setup.
      </p>
    </div>
  )
}

/**
 * The shared shell for a reserved-but-not-live section.
 *
 * One component so all three read identically. The heading states what the
 * section IS, the lead states plainly that it is not live, and the body says
 * what it will do. No spinner, no empty table, no zero-valued card: nothing
 * that could be misread as the feature working and having no data.
 */
function PlaceholderSection ({ icon: Icon, title, lead, testId, children }) {
  return (
    <Card data-testid={testId}>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Icon className="size-4 text-ink-subtle" aria-hidden="true" />
          {title}
        </CardTitle>
        <p className="mt-1 text-sm/relaxed text-ink-muted">{lead}</p>
      </CardHeader>
      <CardBody className="space-y-3 text-sm/relaxed text-ink-muted">
        {children}
      </CardBody>
    </Card>
  )
}
